import React from 'react';
import { useForm, useWatch } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import moment, { Moment } from 'moment';
import styles from './CSSZSickLeaveCertificateConfirmationOfDuration.module.scss';
import DateField from '../../../../Components/Form/components/DateField';
import CheckboxField from '../../../../Components/Form/components/CheckboxField';
import TimePickerField from '../../../../Components/Form/components/TimePickerField/TimePickerField';
import SubmitButton from '../../../../Components/Button/SubmitButton';
import FormWrapper from '../../../../Components/Form/FormWrapper';
import { isExamEndTimeAfterStartTime } from './CSSZSickLeaveCertificateConfirmationOfDuration.helper';

const validMomentRequired = (messageTx: string) =>
	yup
		.mixed<Moment>()
		.required(messageTx)
		.test(messageTx, messageTx, v => moment.isMoment(v) && v.isValid());

/** Form state before mapping API `eventData` to `dateFrom` / `dateTo` (ISO). */
export interface NextExamScheduleFormPart {
	date: string;
	timeFrom: Moment;
	timeTo: Moment;
}

export interface CSSZSickLeaveCertificateConfirmationOfDurationFormValues {
	confirmedToDate: string;
	createdAt: string;
	shouldScheduleEvent: boolean;
	eventData: NextExamScheduleFormPart | null;
}

export interface CSSZSickLeaveCertificateConfirmationOfDurationRequestBody {
	confirmedToDate: string;
	createdAt: string;
	shouldScheduleEvent: boolean;
	eventData: { dateFrom: string; dateTo: string } | null;
}

interface Props {
	onSubmit: (
		values: CSSZSickLeaveCertificateConfirmationOfDurationFormValues,
	) => void;
}

const nextExamEventDataSchema = yup.object({
	date: yup
		.string()
		.required(
			'validation.CSSZSickLeaveCertificateConfirmationOfDuration.nextExam.date',
		),
	timeFrom: validMomentRequired(
		'validation.CSSZSickLeaveCertificateConfirmationOfDuration.nextExam.timeFrom',
	),
	timeTo: validMomentRequired(
		'validation.CSSZSickLeaveCertificateConfirmationOfDuration.nextExam.timeTo',
	).test(
		'time-to-after-from',
		'validation.CSSZSickLeaveCertificateConfirmationOfDuration.nextExam.timeOrder',
		function testTimeOrder(timeTo) {
			const { timeFrom, date } = this.parent as NextExamScheduleFormPart;
			if (
				!moment.isMoment(timeTo) ||
				!timeTo.isValid() ||
				!moment.isMoment(timeFrom) ||
				!timeFrom.isValid() ||
				!date
			) {
				return true;
			}

			return isExamEndTimeAfterStartTime(date, timeFrom, timeTo);
		},
	),
});

const validationScheme = yup.object({
	confirmedToDate: yup
		.string()
		.required(
			'validation.CSSZSickLeaveCertificateConfirmationOfDuration.confirmedToDate',
		),
	createdAt: yup
		.string()
		.required(
			'validation.CSSZSickLeaveCertificateConfirmationOfDuration.createdAt',
		),
	shouldScheduleEvent: yup.boolean().default(false),
	eventData: yup.mixed().when('shouldScheduleEvent', {
		is: true,
		then: () => nextExamEventDataSchema,
	}),
});

const CSSZSickLeaveCertificateConfirmationOfDurationForm: React.FC<Props> = ({
	onSubmit,
}) => {
	const formMethods =
		useForm<CSSZSickLeaveCertificateConfirmationOfDurationFormValues>({
			resolver: yupResolver(validationScheme),
			defaultValues: {
				confirmedToDate: moment().toISOString(),
				createdAt: moment().toISOString(),
				shouldScheduleEvent: false,
				eventData: null,
			},
		});

	const { setValue } = formMethods;

	const planFollowUpExam = useWatch({
		control: formMethods.control,
		name: 'shouldScheduleEvent',
		defaultValue: false,
	});

	return (
		<FormWrapper formMethods={formMethods}>
			<div className={styles.between}>
				<DateField
					name='confirmedToDate'
					label='component.CSSZSickLeaveCertificateConfirmationOfDuration.confirmedToDate'
					isRequired
					isWrapped
				/>
				<DateField
					name='createdAt'
					label='component.CSSZSickLeaveCertificateConfirmationOfDuration.createdAt'
					isRequired
					isWrapped
				/>
			</div>
			<div className={`mt-3 ${styles.optionalSchedule}`}>
				<CheckboxField
					name='shouldScheduleEvent'
					tx='component.CSSZSickLeaveCertificateConfirmationOfDuration.scheduleNextExam'
					isWrapped
					customOnChange={checked => {
						if (checked) {
							setValue('eventData', {
								date: moment().format('YYYY-MM-DD'),
								timeFrom: moment()
									.hours(8)
									.minutes(0)
									.seconds(0)
									.milliseconds(0),
								timeTo: moment().hours(9).minutes(0).seconds(0).milliseconds(0),
							});
						} else {
							setValue('eventData', null);
						}
					}}
				/>
				{planFollowUpExam && (
					<div className={styles.eventInputs}>
						<DateField
							name='eventData.date'
							label='component.CSSZSickLeaveCertificateConfirmationOfDuration.nextExamDate'
							isRequired={planFollowUpExam}
							isWrapped
						/>
						<div className={styles.timesRow}>
							<div className={styles.timeField}>
								<TimePickerField
									name='eventData.timeFrom'
									label='base.shared.timeFrom'
									isRequired={planFollowUpExam}
									isWrapped
								/>
							</div>
							<div className={styles.timeField}>
								<TimePickerField
									name='eventData.timeTo'
									label='base.shared.timeTo'
									isRequired={planFollowUpExam}
									isWrapped
								/>
							</div>
						</div>
					</div>
				)}
			</div>
			<div className='mt-3'>
				<SubmitButton onSubmit={onSubmit} />
			</div>
		</FormWrapper>
	);
};

export default CSSZSickLeaveCertificateConfirmationOfDurationForm;
